import { Component, ElementRef, inject, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import moment from 'moment';
import { ButtonModule } from 'primeng/button';
import { PopoverModule } from 'primeng/popover';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';

// Composants
import { TableBaseComponent, TableColumnDef } from 'src/app/@shared/components/abstract/table-base.component';
import { TableShareButtonComponent } from 'src/app/@shared/components/table-share-button/table-share-button.component';
import { TableColumnPickerComponent } from 'src/app/@shared/components/table-column-picker/table-column-picker.component';
import { AccountEntriesTableFiltersComponent } from '../account-entries-table-filters/account-entries-table-filters.component';
import { AccountEntriesExportDialogComponent } from '../../account-entries-exports/account-entries-export-dialog/account-entries-export-dialog.component';

// Modèles
import { AccountEntryLine } from 'src/app/@shared/models/accounting/account-entry-line.model';
import { AccountEntryExport } from 'src/app/@shared/models/accounting/account-entry-export.model';

// Enums
import { ContentTypesEnum } from 'src/app/@shared/enums/content-types.enum';

import { Logger } from 'src/app/@core/services/logger/logger.service';
import { TableState } from 'src/app/@core/services/table-state.service';

const LOGGER = new Logger('AccountEntriesTableComponent');

@Component({
    selector: 'app-account-entries-table',
    imports: [
        CommonModule,
        RouterModule,
        ButtonModule,
        ProgressSpinnerModule,
        TableModule,
        TooltipModule,
        PopoverModule,
        TableColumnPickerComponent,
        AccountEntriesTableFiltersComponent,
        TableShareButtonComponent,
    ],
    templateUrl: './account-entries-table.component.html',
    styleUrls: ['./account-entries-table.component.scss']
})
export class AccountEntriesTableComponent extends TableBaseComponent {

    /*----------------------------------------------------------------------------------------
     * Colonnes
     ----------------------------------------------------------------------------------------*/

    public override columns: TableColumnDef[] = [
        { field: 'postingDate', header: 'Date', visible: true, sortField: 'accountEntry/postingDate' },
        { field: 'account', header: 'Compte', visible: true, sortField: 'account/account' },
        { field: 'accountLabel', header: 'Libellé du compte', visible: true, sortField: 'account/label' },
        { field: 'debit', header: 'Débit', visible: true, sortField: 'amountOrg', headerClass: 'text-right', cellClass: 'text-right' },
        { field: 'credit', header: 'Crédit', visible: true, sortField: 'amountOrg', headerClass: 'text-right', cellClass: 'text-right' },
        { field: 'label', header: 'Libellé écriture', visible: true, sortField: 'label' },
        { field: 'reference', header: 'Pièce', visible: true, sortField: 'accountEntry/reference' },
        { field: 'journal', header: 'Journal', visible: true, sortField: 'accountEntry/journal/code' },
        { field: 'lettering', header: 'Lettrage', visible: true, sortField: 'lettering/code,account/account' },
        { field: 'letteringDate', header: 'Date de lettrage', visible: true, sortField: 'lettering/date' },
        { field: 'amount', header: 'Montant en devise', visible: true, sortField: 'amount', headerClass: 'text-right', cellClass: 'text-right' },
        { field: 'operationDate', header: 'Date de pièce', visible: true, sortField: 'accountEntry/operationDate' },
    ];

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    @ViewChild(AccountEntriesTableFiltersComponent)
    private filtersComponent!: AccountEntriesTableFiltersComponent;

    private _host = inject(ElementRef);

    /**
     * Objet source des lignes à mettre en évidence, au format `<type>:<identifiant>` (#1135).
     */
    @Input()
    public highlighted: string | null = null;
    @Input()
    public override paginator: boolean = false;
    @Input()
    public showActions: boolean = false;
    @Input()
    public override showFilters: boolean = false;
    @Input()
    public showNavs: boolean = false;
    @Input()
    public showTotals: boolean = false;
    @Input()
    public size: "small" | "large" | undefined = 'small';

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    private _lines: AccountEntryLine[] = [];
    public get lines(): AccountEntryLine[] {
        return this._lines;
    }
    @Input()
    public set lines(lines: AccountEntryLine[]) {
        let emptyArray: AccountEntryLine[] = [];
        this._lines = emptyArray.concat(lines);
        this._scrollToHighlighted();
    }

    @Input()
    public showSeparators: boolean = false;

    /*----------------------------------------------------------------------------------------
     * Méthode publiques
     ----------------------------------------------------------------------------------------*/

    /**
     * Une ligne est mise en évidence quand son écriture a pour source l'objet désigné (#1135).
     */
    public isHighlighted(accountEntryLine: AccountEntryLine): boolean {
        if (!this.highlighted || !accountEntryLine.accountEntry) return false;
        return this.highlighted === `${accountEntryLine.accountEntry.contentTypeModel}:${accountEntryLine.accountEntry.objectId}`;
    }

    public onClickExport(): void {
        this._openExportDialog();
    }

    public onClickLine(accountEntryLine: AccountEntryLine): void {
        this._navToObject(accountEntryLine);
    }

    public get totalCredit(): number {
        return this.lines.reduce((acc, line) => acc + (line.debitCredit.code === 'credit' ? line.amountOrg : 0), 0);
    }

    public get totalDebit(): number {
        return this.lines.reduce((acc, line) => acc + (line.debitCredit.code === 'debit' ? line.amountOrg : 0), 0);
    }

    /*----------------------------------------------------------------------------------------
     * Méthode protégées
     ----------------------------------------------------------------------------------------*/

    protected override modifyTableState(state: TableState): void {
        // Désactiver la sauvegarde de l'état du tableau (pagination uniquement, pas le tri)
        state.sortField = undefined;
        state.sortOrder = undefined;
    }

    protected override subscribeToObservables(): void {
        this.subscriptions.add(
            this.accountEntryLineService.fetching$.subscribe(fetching => this.loading = fetching)
        );
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _openExportDialog(): void {
        // Récupération des valeurs des filtres depuis le composant de filtres
        const journalsSelected = this.filtersComponent.journalsSelected;
        const operationDateRange = this.filtersComponent.operationDateRange;
        const extractedFilter = this.filtersComponent.extractedFilter;

        // Initialisation de l'export
        const accountEntryExport = AccountEntryExport.init();
        accountEntryExport.extracted = extractedFilter;

        // Gestion des dates
        if (operationDateRange && operationDateRange[0] && operationDateRange[1]) {
            accountEntryExport.dateFrom = moment(operationDateRange[0]);
            accountEntryExport.dateTo = moment(operationDateRange[1]);
        } else {
            accountEntryExport.dateFrom = null;
            accountEntryExport.dateTo = null;
        }

        // Gestion des journaux
        if (journalsSelected && journalsSelected.length > 0) {
            accountEntryExport.journals = journalsSelected.map(j => j.code);
        } else {
            accountEntryExport.journals = [];
        }

        // Ouverture de la modale contenant le formulaire d'export
        this.openDialog(AccountEntriesExportDialogComponent, 'ACCOUNT_ENTRY_LINE_EXPORT', { export: accountEntryExport });
    }

    private _navToBankAccountLine(accountEntryLine: AccountEntryLine): void {
        // Chargement des données complètes de la ligne de compte bancaire
        this.bankAccountLineService.get$(accountEntryLine.accountEntry.objectId).subscribe((bankAccountLine) => {
            // Navigation vers la ligne de compte bancaire dans un nouvel onglet
            const url = this.router.serializeUrl(
                this.router.createUrlTree([this.appService.clientUrl('/banking/bank-account-lines')], { queryParams: { selected: bankAccountLine.id } })
            );
            window.open(url, '_blank');
        });
    }

    private _navToObject(accountEntryLine: AccountEntryLine): void {

        switch (accountEntryLine.accountEntry.contentTypeModel) {
            case ContentTypesEnum.BankAccountLine:
                this._navToBankAccountLine(accountEntryLine);
                break;
            case ContentTypesEnum.PurchaseInvoice:
                this._navToPurchaseInvoice(accountEntryLine);
                break;
            case ContentTypesEnum.SalesInvoice:
                this._navToSalesInvoice(accountEntryLine);
                break;
            default:
                LOGGER.warn('Type d\'objet non géré pour la navigation', accountEntryLine.accountEntry.contentTypeModel);
                break;
        }
    }

    private _navToPurchaseInvoice(accountEntryLine: AccountEntryLine): void {
        const url = this.router.serializeUrl(
            this.router.createUrlTree([this.appService.clientUrl('/purchasing/invoice'), accountEntryLine.accountEntry.objectId])
        );
        window.open(url, '_blank');
    }

    private _navToSalesInvoice(accountEntryLine: AccountEntryLine): void {
        const url = this.router.serializeUrl(
            this.router.createUrlTree([this.appService.clientUrl('/sales/invoice'), accountEntryLine.accountEntry.objectId])
        );
        window.open(url, '_blank');
    }

    private _scrollToHighlighted(): void {
        if (!this.highlighted || !this._lines.some(line => this.isHighlighted(line))) return;
        // Attendre le rendu des lignes avant de défiler jusqu'à la première mise en évidence
        setTimeout(() => {
            const row = (this._host.nativeElement as HTMLElement).querySelector('tr[data-highlighted="true"]');
            row?.scrollIntoView({ block: 'center' });
        }, 1);
    }
}
